/** Client for the one-pager edit loop (/api/collateral). */
import type { Customer } from './api';

export type Target = { pageIdx: number; blockIdx: number } | { pageIdx: number; fieldId: string };

export interface Revision {
  seq: number;
  kind: 'create' | 'revision';
  note?: string;
  source?: 'author' | 'direct' | 'ai';
  ts: string;
}
export interface History {
  headSeq: number;
  revisions: Revision[];
}

/** The Doc is a structured page/block tree; we keep it loosely typed on the client. */
export type Doc = { title: string; footerCaption?: string; pages: DocPage[] };
export type DocPage = { layout: 'cover' | 'body' | 'closing' } & Record<string, unknown>;
export type PatchOp = { op: 'replace' | 'add' | 'remove'; path: string; value?: unknown };

export interface Session {
  name: string;
  doc: Doc;
  html: string;
  pdfUrl: string;
  headSeq: number;
  history: History;
  warnings?: string[];
}
export interface PatchResult {
  ok?: boolean;
  source?: 'direct' | 'ai';
  patch?: PatchOp[];
  doc?: Doc;
  html?: string;
  pdfUrl?: string;
  error?: string;
}

async function jpost<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const data = (await r.json().catch(() => ({}))) as T & { error?: string; ok?: boolean };
  // A non-2xx OR an {error} payload is a failure — never let it flow on as data.
  // (patch/preview returns {ok:false,error} on a rejected edit; callers handle that.)
  if (!r.ok && data.ok === undefined) throw new Error(data.error || `${r.status}`);
  return data;
}

export interface DocSummary {
  name: string;
  title: string;
  headSeq: number;
}

/** List saved one-pager edit sessions. */
export async function listDocs(): Promise<DocSummary[]> {
  const r = await fetch('/api/collateral/docs', { credentials: 'include' });
  const d = (await r.json().catch(() => ({ docs: [] }))) as { docs?: DocSummary[] };
  return d.docs ?? [];
}

/** Reopen a saved one-pager (its current HEAD) into an edit session. */
export async function openDoc(name: string): Promise<Session> {
  const r = await fetch(`/api/collateral/doc/${encodeURIComponent(name)}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`could not open "${name}"`);
  return r.json() as Promise<Session>;
}

export function createOnePager(customer: Customer, brief: string, audience: string): Promise<Session> {
  return jpost<Session>('/api/collateral/onepager', { customer, brief, audience });
}

/** Preview a DIRECT patch (no commit). */
export function patchDirect(name: string, patch: PatchOp[]): Promise<PatchResult> {
  return jpost<PatchResult>(`/api/collateral/doc/${encodeURIComponent(name)}/patch`, { patch });
}

/** Preview an AI edit — "describe a change" scoped to one node. */
export function patchAi(name: string, target: Target, instruction: string): Promise<PatchResult> {
  return jpost<PatchResult>(`/api/collateral/doc/${encodeURIComponent(name)}/patch`, { target, instruction });
}

export function commitPatch(name: string, patch: PatchOp[], note: string, source: 'direct' | 'ai'): Promise<Session> {
  return jpost<Session>(`/api/collateral/doc/${encodeURIComponent(name)}/commit`, { patch, note, source });
}

export function checkoutRev(name: string, seq: number): Promise<Session> {
  return jpost<Session>(`/api/collateral/doc/${encodeURIComponent(name)}/checkout`, { seq });
}

/** Read the node a target addresses, from a Doc. */
export function nodeAt(doc: Doc, t: Target): unknown {
  const page = doc.pages[t.pageIdx] as Record<string, unknown> | undefined;
  if (!page) return undefined;
  if ('blockIdx' in t) return (page.blocks as unknown[])?.[t.blockIdx];
  // fieldId: 'title' | 'subtitle' | 'eyebrow' | 'body' | 'stats.N' | 'terms.N'
  const [field, idx] = t.fieldId.split('.');
  return idx === undefined ? page[field] : (page[field] as unknown[])?.[Number(idx)];
}

export function targetPath(t: Target): string {
  if ('blockIdx' in t) return `/pages/${t.pageIdx}/blocks/${t.blockIdx}`;
  return `/pages/${t.pageIdx}/${t.fieldId.replace('.', '/')}`;
}

/** A short human label for a target, for the popover header. */
export function targetLabel(doc: Doc, t: Target): string {
  const node = nodeAt(doc, t) as Record<string, unknown> | string | undefined;
  if ('blockIdx' in t) {
    const kind = (node as Record<string, unknown>)?.kind;
    return `${String(kind ?? 'block')} · block ${t.blockIdx + 1}`;
  }
  return t.fieldId.replace('.', ' ');
}
